import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { catchError, EMPTY, merge, Observable, Subject, takeUntil, tap } from 'rxjs';
import {
  DmsIntegrationStateSelectors,
  FileDisplayStatus,
  Logger,
  TrackService,
  UploadFileJobCategorizationDisplayStatus,
  UploadFileJobFlattened,
  UploadFileStatus,
  UploadType,
} from '@aiwyn/shared';
import { ResetState } from '../../../shared/state/meta.actions';
import { getOriginalBoxFileName } from '../../client-requests/models';
import { CrlCategorizationFile, CrlCategorizationStatus } from '../models';
import { ClientRequestServiceApiBridge } from '../services/client-request-service-api.bridge';
import { CxpTasksPermissionsService } from '../services/cxp-tasks-permissions.service';
import {
  ReceiveCategorizationFiles,
  ReceiveItemsWithComments,
  RemoveCategorizationFile,
  StartCrlSubscriptions,
  StopCrlSubscriptions,
} from './cxp-crl-subscriptions.actions';
import {
  CRL_CATEGORIZATION_SUBSCRIPTION,
  CRL_COMMENTS_SUBSCRIPTION,
  CxpCrlSubscriptionsStateModel,
  DEFAULT_CXP_CRL_SUBSCRIPTIONS_STATE,
} from './cxp-crl-subscriptions.model';

export const CXP_CRL_SUBSCRIPTIONS_STATE_NAME = 'cxpCrlSubscriptions';

/**
 * How long a bulk row may sit in the categorizing state before we give up waiting
 * on the background process and settle it as uncategorized. Mirrors the legacy
 * `FILE_CATEGORIZATION_TIMEOUT` (client-requests.state) — kept as a local constant
 * because that one is not exported.
 */
export const CRL_FILE_CATEGORIZATION_TIMEOUT_MS = 60_000;

const TERMINAL_STATUSES: ReadonlySet<CrlCategorizationStatus> = new Set([
  CrlCategorizationStatus.CATEGORIZED,
  CrlCategorizationStatus.UNCATEGORIZED,
]);

const STATUS_TO_DISPLAY: Record<CrlCategorizationStatus, FileDisplayStatus> = {
  [CrlCategorizationStatus.UPLOADING]: FileDisplayStatus.UPLOADING,
  [CrlCategorizationStatus.CATEGORIZING]: FileDisplayStatus.CATEGORIZING,
  [CrlCategorizationStatus.CATEGORIZED]: FileDisplayStatus.CATEGORIZED,
  [CrlCategorizationStatus.UNCATEGORIZED]: FileDisplayStatus.UNCATEGORIZED,
};

/**
 * General manager for the CRL page's live subscriptions: the categorization live
 * query and the comments live query, both keyed on the open request. They are
 * declared together on purpose — one WebSocket serves the whole app
 * (`extended-apollo.service.ts` splits subscriptions onto it), so each addition
 * costs an operation, not a socket, and keeping them side by side is what makes
 * that count visible. Further page-level live queries (activity) belong here too.
 *
 * Teardown uses one page-scoped cancel subject covering every stream: a new
 * `StartCrlSubscriptions` emits on it to stop the previous request's queries
 * before opening the new ones, and a same-id re-dispatch is ignored so opening
 * the item overlay (an aux route) never churns them.
 */
@State<CxpCrlSubscriptionsStateModel>({
  name: CXP_CRL_SUBSCRIPTIONS_STATE_NAME,
  defaults: DEFAULT_CXP_CRL_SUBSCRIPTIONS_STATE,
})
@Injectable()
export class CxpCrlSubscriptionsState {
  private readonly bridge = inject(ClientRequestServiceApiBridge);
  private readonly store = inject(Store);
  private readonly logger = inject(Logger);
  private readonly trackService = inject(TrackService);
  private readonly permissionsService = inject(CxpTasksPermissionsService);

  private readonly pageTeardown$ = new Subject<void>();
  private readonly categorizationTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** File ids with a delete in flight — filtered out of emissions so a removed row can't transiently reappear. */
  private readonly pendingRemovalFileIds = new Set<number>();

  @Action(ResetState)
  resetState({ setState }: StateContext<CxpCrlSubscriptionsStateModel>): void {
    this.teardownPageSubscriptions();
    setState(DEFAULT_CXP_CRL_SUBSCRIPTIONS_STATE);
  }

  @Action(StartCrlSubscriptions)
  startCrlSubscriptions(
    ctx: StateContext<CxpCrlSubscriptionsStateModel>,
    { requestId }: StartCrlSubscriptions,
  ): Observable<unknown> {
    // A guest gets no comments surface at all, so its live query never opens.
    const opensComments = this.permissionsService.getPermissionsSnapshot().canViewComments;
    const subscriptions = opensComments
      ? [CRL_CATEGORIZATION_SUBSCRIPTION, CRL_COMMENTS_SUBSCRIPTION]
      : [CRL_CATEGORIZATION_SUBSCRIPTION];

    const state = ctx.getState();
    if (state.requestId === requestId && subscriptions.every((key) => state.activeSubscriptions.includes(key))) {
      return EMPTY;
    }

    this.teardownPageSubscriptions();
    ctx.patchState({
      requestId,
      categorization: { files: [] },
      comments: { withComments: [], withUnviewed: [], latestMessageIdByItem: {} },
      activeSubscriptions: subscriptions,
    });

    // Categorization live query — replaces the file snapshot on each emission.
    const categorization$ = this.bridge.watchRequestSubmissionFiles(requestId).pipe(
      tap((files) => ctx.dispatch(new ReceiveCategorizationFiles(requestId, files))),
      catchError((error) => {
        this.logger.error('[CxpCrlSubscriptions] categorization subscription error', error);
        return EMPTY;
      }),
    );

    // Comments live query — replaces the commented/unviewed item-id snapshot.
    const comments$ = opensComments
      ? this.bridge.watchItemsWithComments(requestId).pipe(
          tap((comments) => ctx.dispatch(new ReceiveItemsWithComments(requestId, comments))),
          catchError((error) => {
            this.logger.error('[CxpCrlSubscriptions] comments subscription error', error);
            return EMPTY;
          }),
        )
      : EMPTY;

    // The DMS upload-jobs list is the SECOND reconcile trigger: the backend creates
    // the CATEGORIZING row (Hasura push) BEFORE the upload HTTP response returns, so
    // at emission time the toast job is still LOADING. Reconciling off the jobs list
    // as well arms the timeout once the job reaches LOADED + CATEGORIZING even if no
    // further subscription emission ever arrives (the fault the timeout exists to catch).
    const dmsJobs$ = this.store.select(DmsIntegrationStateSelectors.getUploadFileJobs).pipe(
      tap(() => this.reconcileToastJobs(requestId)),
      catchError((error) => {
        this.logger.error('[CxpCrlSubscriptions] dms jobs reconcile error', error);
        return EMPTY;
      }),
    );

    return merge(categorization$, comments$, dmsJobs$).pipe(takeUntil(this.pageTeardown$));
  }

  @Action(StopCrlSubscriptions)
  stopCrlSubscriptions(ctx: StateContext<CxpCrlSubscriptionsStateModel>): void {
    this.teardownPageSubscriptions();
    ctx.patchState({
      requestId: null,
      categorization: { files: [] },
      comments: { withComments: [], withUnviewed: [], latestMessageIdByItem: {} },
      activeSubscriptions: [],
    });
  }

  @Action(ReceiveCategorizationFiles)
  receiveCategorizationFiles(
    ctx: StateContext<CxpCrlSubscriptionsStateModel>,
    { requestId, files }: ReceiveCategorizationFiles,
  ): void {
    if (ctx.getState().requestId !== requestId) return;

    // Keep a locally-removed row from transiently reappearing if an emission lands
    // before the backend soft-delete propagates.
    const visibleFiles =
      this.pendingRemovalFileIds.size === 0 ? files : files.filter((file) => !this.pendingRemovalFileIds.has(file.fileId));

    ctx.patchState({ categorization: { files: visibleFiles } });
    this.reconcileToastJobs(requestId);
  }

  @Action(ReceiveItemsWithComments)
  receiveItemsWithComments(
    ctx: StateContext<CxpCrlSubscriptionsStateModel>,
    { requestId, comments }: ReceiveItemsWithComments,
  ): void {
    if (ctx.getState().requestId !== requestId) return;
    ctx.patchState({ comments });
  }

  @Action(RemoveCategorizationFile)
  removeCategorizationFile(
    ctx: StateContext<CxpCrlSubscriptionsStateModel>,
    { fileId }: RemoveCategorizationFile,
  ): Observable<unknown> {
    const { requestId, categorization } = ctx.getState();
    if (requestId === null) return EMPTY;

    this.pendingRemovalFileIds.add(fileId);
    const previousFiles = categorization.files;
    ctx.patchState({ categorization: { files: previousFiles.filter((file) => file.fileId !== fileId) } });

    return this.bridge.removeSubmissionFile(requestId, fileId).pipe(
      tap(() => this.pendingRemovalFileIds.delete(fileId)),
      catchError((error) => {
        // Roll back: drop the guard so the row returns via the next emission, and
        // restore the pre-removal snapshot immediately.
        this.pendingRemovalFileIds.delete(fileId);
        ctx.patchState({ categorization: { files: previousFiles } });
        this.logger.error('[CxpCrlSubscriptions] remove submission file failed', error);
        return EMPTY;
      }),
    );
  }

  /**
   * Correlates each in-flight bulk upload toast row (LOADED, still categorizing,
   * scoped to THIS request) to its subscription file — by captured `fileId` first,
   * else the timestamped upload filename — and drives the row to its terminal
   * display status. A row still categorizing gets a per-file timeout that settles
   * it as uncategorized (plus an anomaly event) if the background process never
   * resolves. Runs on both triggers (subscription emissions and DMS-job changes).
   */
  private reconcileToastJobs(requestId: number): void {
    const files = this.snapshotFiles();
    const categorizingJobs = this.store
      .selectSnapshot(DmsIntegrationStateSelectors.getUploadFileJobs)
      .filter(
        (job) =>
          job.entityData.uploadType === UploadType.CLIENT_REQUEST_SUBMISSION &&
          job.entityData.clientRequestId === requestId &&
          job.status === UploadFileStatus.LOADED &&
          job.data?.status === FileDisplayStatus.CATEGORIZING,
      );
    if (categorizingJobs.length === 0) return;

    const filesByFileId = new Map(files.filter((file) => file.fileId != null).map((file) => [file.fileId, file]));
    const filesByName = new Map(files.map((file) => [file.name, file]));

    for (const job of categorizingJobs) {
      const match = this.correlateFile(job, filesByFileId, filesByName);

      if (match && TERMINAL_STATUSES.has(match.status)) {
        this.clearTimer(job.jobId);
        this.store.dispatch(new UploadFileJobCategorizationDisplayStatus(job.jobId, STATUS_TO_DISPLAY[match.status]));
      } else {
        this.ensureTimer(job.jobId, requestId, job.data?.fileId ?? null);
      }
    }
  }

  private correlateFile(
    job: UploadFileJobFlattened,
    filesByFileId: Map<number, CrlCategorizationFile>,
    filesByName: Map<string, CrlCategorizationFile>,
  ): CrlCategorizationFile | undefined {
    const byFileId = job.data?.fileId != null ? filesByFileId.get(job.data.fileId) : undefined;
    return byFileId ?? filesByName.get(getOriginalBoxFileName(job.calculatedName));
  }

  private ensureTimer(jobId: string, requestId: number, fileId: number | null): void {
    if (this.categorizationTimers.has(jobId)) return;

    const timer = setTimeout(() => {
      this.categorizationTimers.delete(jobId);
      this.store.dispatch(new UploadFileJobCategorizationDisplayStatus(jobId, FileDisplayStatus.UNCATEGORIZED));
      this.logger.error('[CxpCrlSubscriptions] file categorization timed out', { jobId, requestId, fileId });
      this.trackService.track('crl_categorization_timed_out', { requestId, fileId: fileId ?? 0 });
    }, CRL_FILE_CATEGORIZATION_TIMEOUT_MS);

    this.categorizationTimers.set(jobId, timer);
  }

  private clearTimer(jobId: string): void {
    const timer = this.categorizationTimers.get(jobId);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.categorizationTimers.delete(jobId);
    }
  }

  /** Stops every live query the page has open and drops their in-flight bookkeeping. */
  private teardownPageSubscriptions(): void {
    this.pageTeardown$.next();
    this.categorizationTimers.forEach((timer) => clearTimeout(timer));
    this.categorizationTimers.clear();
    this.pendingRemovalFileIds.clear();
  }

  private snapshotFiles(): CrlCategorizationFile[] {
    return this.store.selectSnapshot(
      (state: Record<string, CxpCrlSubscriptionsStateModel>) => state[CXP_CRL_SUBSCRIPTIONS_STATE_NAME].categorization.files,
    );
  }
}
